import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CallAPI } from '@/services/utils';
import { extractFlatAssets } from '@unianlx-support/ua-ui-lib/dist/utils/userAsset';

let cachedUserId = null;
let cachedUserAssets = null;
let inflightPromise = null;

const useCustomAssets = () => {
  const { user } = useSelector(state => state.userDetails);
  const userId = user?.id ?? '2';
  const [userAssets, setUserAssets] = useState(
    () => (cachedUserId === userId && cachedUserAssets ? cachedUserAssets : [])
  );
  const [refreshTick, setRefreshTick] = useState(0);

  // The host's Asset Manager saved or deleted an asset (bridge event): drop the cache and take the latest list.
  useEffect(() => {
    const onAssetsChanged = () => {
      cachedUserAssets = null;
      inflightPromise = null;
      setRefreshTick(tick => tick + 1);
    };
    window.addEventListener('ab:userAssetsChanged', onAssetsChanged);
    return () => window.removeEventListener('ab:userAssetsChanged', onAssetsChanged);
  }, []);

  useEffect(() => {
    if (cachedUserId === userId && cachedUserAssets) {
      setUserAssets(cachedUserAssets);
      return;
    }

    if (!inflightPromise || cachedUserId !== userId) {
      const requestUserId = userId;
      if (cachedUserId !== userId) {
        cachedUserAssets = null;
      }
      cachedUserId = userId;
      inflightPromise = CallAPI('alloc-tool-admin/fetch-details', 'POST', {}, {
        user_id: userId,
        app_name: 'Alloc Tool'
      })
        .then(res => {
          if (!res?.data) return [];
          const appSettingStr = res.data.app_setting;
          if (!appSettingStr) return [];
          let parsed;
          try { parsed = JSON.parse(appSettingStr); } catch { return []; }
          const flatData = extractFlatAssets(parsed?.customAssets || {});
          if (cachedUserId === requestUserId) {
            cachedUserAssets = flatData;
          }
          return flatData;
        })
        .catch(err => {
          console.error('Error fetching user assets:', err);
          return [];
        })
        .finally(() => {
          if (cachedUserId === requestUserId) {
            inflightPromise = null;
          }
        });
    }

    let cancelled = false;
    inflightPromise.then((flatData) => {
      if (!cancelled && Array.isArray(flatData)) {
        setUserAssets(flatData);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, refreshTick]);

  return userAssets;
};

export default useCustomAssets;
